import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { SiteHeader } from "@/components/SiteHeader";
import { FreePreview } from "@/components/FreePreview";
import { PublicIntroVideo } from "@/components/PublicIntroVideo";
import { createCheckoutSession } from "@/lib/stripe.functions";

export const Route = createFileRoute("/courses/business-and-accountancy/basic-business-accountancy")({
  head: () => {
    const title = "The Basic Business Accountancy Course — WHO KNEW";
    const description =
      "A course on reading UK company accounts, built for people who've spent years nodding along to numbers in meetings without quite knowing what they meant. £99, lifetime access, built for the UK.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/courses/business-and-accountancy/basic-business-accountancy" },
      ],
      links: [
        {
          rel: "preload",
          as: "image",
          href: "https://image.mux.com/c0200KfqJF2XZjU029WmIMpHrhq02giwbU7dJVn02Sww8TB4/thumbnail.webp",
        },
      ],
    };
  },
  component: Index,
});

function Index() {
  const [showSticky, setShowSticky] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const startCheckout = useServerFn(createCheckoutSession);
  // Per-mount nonce — dedupes anonymous double-clicks on this page-load.
  const clientNonceRef = useRef<string>(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );

  const handleCheckout = async () => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const res = await startCheckout({ data: { clientNonce: clientNonceRef.current } });
      if (res.ok) {
        window.location.assign(res.url);
        return;
      }
      if (res.error === "rate_limited") {
        const { formatRateLimitMessage } = await import("@/lib/rate-limit-message");
        setCheckoutError(formatRateLimitMessage(res.retryAfterSeconds));
        return;
      }
      setCheckoutError("Couldn't start checkout. Please try again or email hello@whoknew.guide.");
    } catch {
      setCheckoutError("Couldn't start checkout. Please try again or email hello@whoknew.guide.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const doc = document.documentElement;
      const footer = document.getElementById("site-footer");
      const footerTop = footer
        ? footer.getBoundingClientRect().top + window.scrollY
        : doc.scrollHeight;
      const nearFooter = y + window.innerHeight > footerTop - 40;
      setShowSticky(y > 800 && !nearFooter);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-paper text-ink">
      <SiteHeader />

      <main className="pl-6 pr-6 md:pl-[160px] md:pr-12">
        {/* 1. HERO */}
        <section className="max-w-[720px] pt-[140px] md:pt-[180px]">
          <p
            className="text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            A WHO KNEW COURSE
          </p>

          <h1 className="mt-8 text-[28px] md:text-[44px] font-bold leading-[1.15] text-ink">
            The Basic Business Accountancy Course for People Who Will Not, Under Any
            Circumstances, Take an Accountancy Course
          </h1>

          <p className="mt-8 text-[18px] md:text-[22px] italic leading-[1.4] text-muted-foreground">
            Six modules. Five real UK case studies. One bakery chain you'll come to love.
          </p>

          <hr className="mt-10 w-[60px] border-0 h-[2px] bg-burgundy" />

          <div className="mt-10 space-y-6 text-[18px] leading-[1.6] text-ink">
            <p>
              For founders, marketers, salespeople, and anyone else who has spent years
              nodding at numbers in meetings without being entirely sure what they were
              nodding at.
            </p>
            <p>
              By the end, you'll read a full set of UK accounts in under thirty minutes.
              Spot the tricks. Understand what your competitors are actually doing. Built
              for the UK — not the US — so you can use it the day after you finish.
            </p>
          </div>

          <p className="mt-8 mb-8 text-[18px] italic leading-[1.6] text-muted-foreground">
            Total runtime: 2 hours 28 minutes. Built to fit in a Saturday afternoon.
          </p>

          <div>
            <button
              type="button"
              onClick={handleCheckout}
              disabled={checkoutLoading}
              className="interactive-lift inline-block w-full md:w-auto bg-burgundy text-paper px-8 py-4 rounded-[2px] text-[16px] font-bold uppercase tracking-[0.05em] hover:bg-burgundy-2 transition-colors text-center disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              {checkoutLoading ? "Starting…" : "Start the course"}
            </button>
            <p
              className="mt-4 text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              One-time payment · £99 · Lifetime access
            </p>
            <p
              className="text-[16px] italic"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                color: '#5A534A',
                lineHeight: 1.5,
                marginTop: '12px',
                marginBottom: '24px',
              }}
            >
              Roughly the price of dinner with an accountant. A fraction of any qualification. Yours forever.
            </p>
            {checkoutError && (
              <p className="mt-2 text-[14px] italic text-burgundy" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                {checkoutError}
              </p>
            )}
            <p className="mt-4 text-[16px] italic text-ink">
              <a
                href="#free-preview"
                className="underline decoration-burgundy/40 underline-offset-4 hover:decoration-burgundy decoration-1 hover:decoration-2 transition-all"
              >
                or watch Lesson 1.1 free
              </a>
            </p>
          </div>
        </section>

        {/* 2. COURSE INTRO (public, no quiz) */}
        <PublicIntroVideo
          playbackId="OH02wn9XeVCHm02HFjNDqD9wsSa4utftwDHX463cik01vg"
          eyebrow="Course intro"
          title="What this is."
          duration="2 minutes 20 seconds"
          anchorId="course-intro"
        />

        {/* 3. FREE PREVIEW */}
        <FreePreview />

        {/* 3. WHAT YOU'LL BE ABLE TO DO */}
        <section className="max-w-[720px] pt-[64px] md:pt-[96px]">
          <hr className="w-[60px] border-0 h-[2px] bg-burgundy" />
          <p
            className="mt-6 text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            What you'll be able to do
          </p>
          <h2 className="mt-6 text-[24px] md:text-[32px] font-bold leading-[1.2] text-ink">
            Four things you'll do by the end. None of which you can do now.
          </h2>

          <div className="mt-12 space-y-10">
            {outcomes.map((o) => (
              <div key={o.number}>
                <p
                  className="text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  {o.number}
                </p>
                <h3 className="mt-3 text-[18px] md:text-[22px] font-bold leading-[1.2] text-ink">
                  {o.title}
                </h3>
                <p className="mt-4 text-[18px] leading-[1.6] text-ink">{o.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 4. WHY THIS COURSE EXISTS */}
        <section className="max-w-[720px] pt-[64px] md:pt-[96px]">
          <hr className="w-[60px] border-0 h-[2px] bg-burgundy" />
          <p
            className="mt-6 text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            Why this exists
          </p>
          <h2 className="mt-6 text-[24px] md:text-[32px] font-bold leading-[1.2] text-ink">
            Most online business courses are American. This one isn't.
          </h2>

          <div className="mt-8 space-y-6 text-[18px] leading-[1.6] text-ink">
            <p>
              If you're in UK business and you want to read a set of accounts, your
              options are not great.
            </p>
            <p>
              Most online courses teach US GAAP. The SEC filings of US companies.
              American tax. None of that helps when the company you're trying to
              understand is filed at Companies House on a Tuesday morning in Cardiff. The
              UK has its own accounting standards, its own filing rules, its own audit
              regime, and its own particular ways of hiding things. This course teaches
              those.
            </p>
            <p>
              The other option is a real accountancy qualification. AAT, ACCA, ICAEW. Two
              to four years of study, several thousand pounds, and at the end of it
              you're an accountant. Which is great, if that's what you wanted. Most
              people in business don't. They just want to read the numbers without it
              becoming a career change.
            </p>
            <p>
              This is the middle option. The one that didn't exist. Six modules, two and
              a half hours, built for people who want to be <em>numerate</em>, not{" "}
              <em>qualified</em>. Built for the UK, because that's where the accounts
              are.
            </p>
          </div>
        </section>

        {/* 5. WHAT'S INSIDE */}
        <section className="max-w-[720px] pt-[64px] md:pt-[96px]">
          <hr className="w-[60px] border-0 h-[2px] bg-burgundy" />
          <p
            className="mt-6 text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            What's inside
          </p>
          <h2 className="mt-6 text-[24px] md:text-[32px] font-bold leading-[1.2] text-ink">
            Six modules. 2 hours 28 minutes of video. A worked example you can actually use.
          </h2>
          <p className="mt-6 text-[18px] leading-[1.6] text-ink">
            Each module has four teaching lessons, a UK case study, a real-world activity
            using Companies House, a downloadable workbook, and an Excel template. Quizzes
            after every teaching lesson so you actually retain it.
          </p>

          <div className="mt-12 space-y-12">
            {modules.map((m) => (
              <div key={m.number}>
                <hr className="border-0 h-px bg-burgundy" />
                <p
                  className="mt-6 text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  Module {m.number} — {m.title}
                </p>
                <p className="mt-2 text-[16px] italic text-muted-foreground">
                  {m.runtime} total runtime
                </p>
                <ul className="mt-6 space-y-3">
                  {m.lessons.map((l) => (
                    <li
                      key={l.title}
                      className="flex items-baseline gap-3 text-[16px] leading-[1.5] text-ink"
                    >
                      <span className="min-w-0 flex-1">{l.title}</span>
                      <span className="hidden sm:block flex-shrink-0 flex-grow-0 basis-8 border-b border-dotted border-rule translate-y-[-4px]" />
                      <span className="shrink-0 text-muted-foreground tabular-nums">
                        {l.time}
                      </span>
                    </li>
                  ))}
                </ul>
                <p
                  className="mt-6 text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  {m.includes}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16">
            <button
              type="button"
              onClick={handleCheckout}
              disabled={checkoutLoading}
              className="interactive-lift inline-block w-full md:w-auto bg-burgundy text-paper px-8 py-4 rounded-[2px] text-[16px] font-bold uppercase tracking-[0.05em] hover:bg-burgundy-2 transition-colors text-center disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              {checkoutLoading ? "Starting…" : "Start the course"}
            </button>
            <p
              className="mt-4 text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              One-time payment · £99 · Lifetime access
            </p>
          </div>
        </section>

        {/* 6. WHO THIS IS FOR */}
        <section className="max-w-[720px] pt-[64px] md:pt-[96px]">
          <hr className="w-[60px] border-0 h-[2px] bg-burgundy" />
          <p
            className="mt-6 text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            Who this is for
          </p>
          <h2 className="mt-6 text-[24px] md:text-[32px] font-bold leading-[1.2] text-ink">
            And a few people it isn't for.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-x-12">
            <div>
              <p
                className="text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                Who this is for
              </p>
              <div className="mt-6 space-y-6 text-[18px] leading-[1.6] text-ink">
                {forList.map((t, i) => (<p key={i}>{t}</p>))}
              </div>
            </div>
            <div>
              <p
                className="text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                Who this isn't for
              </p>
              <div className="mt-6 space-y-6 text-[18px] leading-[1.6] text-ink">
                {notForList.map((t, i) => (<p key={i}>{t}</p>))}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* 7. SIGN-OFF + FINAL CTA */}
      <section className="max-w-[720px] mx-auto pt-[64px] md:pt-[96px] px-6 md:px-0 text-center">
        <h2 className="text-[26px] md:text-[36px] font-bold leading-[1.2] text-ink">
          Who Knew? Not us. Not you. Not yet.
        </h2>
        <h2 className="mt-2 text-[26px] md:text-[36px] font-bold leading-[1.2] text-ink">
          The 'not you' bit is about to change.
        </h2>
        <p
          className="mt-4 text-[14px] md:text-[16px] italic leading-[1.4] text-center"
          style={{ fontFamily: '"Cormorant Garamond", serif', color: '#5A534A' }}
        >
          (We knew, obviously. We made the whole thing. 'Not us' was just funnier.)
        </p>
        <div className="mt-12 flex flex-col items-center">
          <button
            type="button"
            onClick={handleCheckout}
            disabled={checkoutLoading}
            className="interactive-lift inline-block bg-burgundy text-paper px-10 py-5 rounded-[2px] text-[16px] font-bold uppercase tracking-[0.05em] hover:bg-burgundy-2 transition-colors text-center disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            {checkoutLoading ? "Starting…" : "Start the course"}
          </button>
          <p
            className="mt-4 text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            One-time payment · £99 · Lifetime access · 14-day refund
          </p>
        </div>
      </section>

      {/* 8. FOOTER */}
      <footer id="site-footer" className="mt-[96px] w-full flex flex-col items-center">
        <hr className="border-0 h-px bg-burgundy w-full max-w-[720px]" />
        <p
          className="mt-6 font-bold uppercase tracking-[0.1em] text-burgundy text-[16px] text-center"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          WHO KNEW
        </p>
        <p
          className="mt-3 font-bold uppercase tracking-[0.05em] text-[10px] text-muted-foreground text-center"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          © 2026 WHO KNEW. ALL RIGHTS RESERVED.
        </p>
        <div
          className="mt-4 mb-6 font-bold uppercase tracking-[0.05em] text-[10px] text-burgundy text-center"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          <Link to="/privacy" className="link-hit link-hit-tight hover:underline">Privacy</Link>
          <span className="mx-2">·</span>
          <Link to="/terms" className="link-hit link-hit-tight hover:underline">Terms</Link>
          <span className="mx-2">·</span>
          <Link to="/refunds" className="link-hit link-hit-tight hover:underline">Refunds</Link>
          <span className="mx-2">·</span>
          <Link to="/teams" className="link-hit link-hit-tight hover:underline">For teams</Link>
        </div>

      </footer>

      {/* Sticky right-side CTA card (desktop only) */}
      <aside
        className={`hidden lg:block fixed top-[160px] right-[80px] w-[280px] bg-paper-dark p-6 rounded-[2px] transition-opacity duration-300 z-40 ${showSticky ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!showSticky}
      >
        <p
          className="text-[10px] font-bold uppercase tracking-[0.05em] text-burgundy"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          Ready?
        </p>
        <p className="mt-3 text-[36px] font-bold leading-none text-ink">£99</p>
        <p
          className="mt-1 text-[9px] font-bold uppercase tracking-[0.05em] text-muted-foreground"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          One-time · Lifetime access
        </p>
        <button
          type="button"
          onClick={handleCheckout}
          disabled={checkoutLoading}
          className="interactive-lift mt-5 inline-block w-full bg-burgundy text-paper px-4 py-[14px] rounded-[2px] text-[13px] font-bold uppercase tracking-[0.05em] hover:bg-burgundy-2 transition-colors text-center disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          {checkoutLoading ? "Starting…" : "Start the course"}
        </button>
        <p className="mt-3 text-center text-[13px] italic text-ink">
          <a
            href="#free-preview"
            className="hover:underline hover:decoration-burgundy underline-offset-4"
          >
            Watch Lesson 1.1 free
          </a>
        </p>
      </aside>
    </div>
  );
}

const forList = [
  "Founders who haven't been taught to read their own accounts and are quietly winging it.",
  "Marketers, salespeople, ops people, and anyone in the first few years of a business career who has to sit next to numbers without having been taught what they mean.",
  "Senior people who got promoted before the numbers became their job and are now too senior to ask basic questions out loud.",
  "Investors who'd rather read the actual accounts than the analyst's summary of the actual accounts.",
  "Aspiring board members. Aspiring non-execs. Anyone who'd like to make their first board contribution be something other than nodding.",
  "Anyone who has ever, in a meeting, said 'yep, makes sense' when it absolutely did not make sense.",
];

const notForList = [
  "Qualified accountants. You already know. Skip this. Possibly gift it to a client.",
  "People who want to become accountants. AAT or ACCA is your route, not this. This course will not get you a job in finance — that's not what it's for.",
  "People who want a certificate to put on LinkedIn. There isn't one. We considered it. Decided it would attract the wrong buyer.",
  "People who want US GAAP. Wrong country. Wrong course. Sorry.",
  "People who think the answer to every problem is a more complicated spreadsheet. We will not help.",
];

const outcomes = [
  {
    number: "01",
    title: "Read any UK annual report in under thirty minutes.",
    body: "Not skim. Read. Profit, cash, balance sheet, the notes, the bits the company would rather you skimmed. The first one will take you the full thirty. By the tenth, you'll be doing it in five.",
  },
  {
    number: "02",
    title: "Spot the tricks before they collapse the business.",
    body: "Patisserie Valerie. Carillion. Thomas Cook. Quindell. Each one left clues in the accounts for years before anyone noticed. You'll know the clues. You'll see them in real time, in companies still trading, while everyone around you nods at the numbers.",
  },
  {
    number: "03",
    title: "Understand what your competitors are actually doing.",
    body: "Their accounts are public. Companies House. Free, downloadable, sitting there. By the end of this course you'll read them in the way they're meant to be read — not just turnover and profit but margins, working capital, where the cash is going. Information your competitors assume nobody is looking at.",
  },
  {
    number: "04",
    title: "Quietly become the most numerate person in the room.",
    body: "Not the loudest. Not the one with the qualification. The one who actually reads the numbers before the meeting and asks the question nobody else thought to ask. People notice. It's a slow upgrade to your professional reputation that compounds for the rest of your career.",
  },
];

const modules = [
  {
    number: "01",
    title: "P&L",
    runtime: "21:26",
    lessons: [
      { title: "Lesson 1.1 — What is a P&L", time: "3:53" },
      { title: "Lesson 1.2 — Why profit isn't the same as cash", time: "3:40" },
      { title: "Lesson 1.3 — How your P&L connects to Companies House", time: "4:00" },
      { title: "Lesson 1.4 — Reading Big Companies", time: "4:14" },
      { title: "Lesson 1.5 — Case Study: Carillion", time: "5:39" },
    ],
    includes: "Includes: Workbook · Excel template · Companies House activity",
  },
  {
    number: "02",
    title: "Revenue",
    runtime: "23:46",
    lessons: [
      { title: "Lesson 2.1 — What counts as revenue", time: "4:33" },
      { title: "Lesson 2.2 — When you're allowed to recognise it", time: "4:54" },
      { title: "Lesson 2.3 — Why growing revenue can hide a shrinking business", time: "2:37" },
      { title: "Lesson 2.4 — Reading the Revenue note", time: "5:59" },
      { title: "Lesson 2.5 — Case Study: Patisserie Valerie", time: "5:43" },
    ],
    includes: "Includes: Workbook · Excel template · Companies House activity",
  },
  {
    number: "03",
    title: "Costs",
    runtime: "26:05",
    lessons: [
      { title: "Lesson 3.1 — Cost of sales vs Overheads", time: "5:15" },
      { title: "Lesson 3.2 — The three margins that matter", time: "4:31" },
      { title: "Lesson 3.3 — Spotting hidden costs in the notes", time: "5:02" },
      { title: "Lesson 3.4 — Exceptional Items and when to be suspicious", time: "5:50" },
      { title: "Lesson 3.5 — Case Study: Marks & Spencer", time: "5:27" },
    ],
    includes: "Includes: Workbook · Excel template · Companies House activity",
  },
  {
    number: "04",
    title: "Balance Sheet and Cash Flow",
    runtime: "27:34",
    lessons: [
      { title: "Lesson 4.1 — What the balance sheet always shows", time: "5:50" },
      { title: "Lesson 4.2 — Assets, Liabilities and what counts as equity", time: "5:50" },
      { title: "Lesson 4.3 — Working Capital", time: "4:30" },
      { title: "Lesson 4.4 — The cash flow", time: "5:15" },
      { title: "Lesson 4.5 — Case Study: Thomas Cook", time: "6:09" },
    ],
    includes: "Includes: Workbook · Excel template · Companies House activity",
  },
  {
    number: "05",
    title: "How Tricks and Traps Hide in Accounts",
    runtime: "28:20",
    lessons: [
      { title: "Lesson 5.1 — Why accounts can lie", time: "4:53" },
      { title: "Lesson 5.2 — Revenue Tricks", time: "5:45" },
      { title: "Lesson 5.3 — Cost and Profit tricks", time: "5:55" },
      { title: "Lesson 5.4 — Balance sheet tricks", time: "5:41" },
      { title: "Lesson 5.5 — Case Study: Quindell", time: "6:06" },
    ],
    includes: "Includes: Workbook · Excel template · Companies House activity",
  },
  {
    number: "06",
    title: "Reading a Real Company: Greggs",
    runtime: "14:17",
    lessons: [
      { title: "Lesson 6.1 — Reading a real P&L", time: "4:02" },
      { title: "Lesson 6.2 — Reading the balance sheet", time: "3:32" },
      { title: "Lesson 6.3 — Reading the cash flow", time: "3:53" },
      { title: "Lesson 6.4 — What you do with this", time: "2:50" },
    ],
    includes: "Includes: Entire course workbook · Entire course template",
  },
];
